import { handleResponse, handleError, headerset } from "../apiUtils";
import { OdPLegacy, SeguiOdPLegacyResponse } from "components/main/produzione/odpLegacySlice";
import { FiltriOdPLegacy } from "components/userPreference/userPreferenceSlice";

// ============================================
// BASE URL
// ============================================

const baseUrl = (odpLegacyId?: number | null) => {
    let url: string = process.env.API_URL + "/api/produzione/odp-legacy/";

    if (odpLegacyId) {
        url += odpLegacyId + "/";
    }

    return url;
};

// ============================================
// QUERY STRING BUILDER
// ============================================

const buildQueryString = (filtri: FiltriOdPLegacy, gotoId?: number): string => {
    const params = new URLSearchParams();

    // ========== FILTRI SPECIFICI ==========

    if (filtri.id_documento) {
        params.append("id_documento", filtri.id_documento.join(","));
    }

    if (filtri.esercizio) {
        params.append("esercizio", filtri.esercizio.join(","));
    }

    if (filtri.codice_articolo) {
        params.append("codice_articolo", filtri.codice_articolo.join(","));
    }

    if (filtri.numero_registrazione) {
        params.append("numero_registrazione", filtri.numero_registrazione.join(","));
    }

    if (filtri.stati && filtri.stati.length > 0) {
        params.append("stati", filtri.stati.join(","));
    }

    if (filtri.data_da) {
        params.append("data_da", filtri.data_da);
    }

    if (filtri.data_a) {
        params.append("data_a", filtri.data_a);
    }

    if (filtri.cliente?.id) {
        params.append("cliente_id", filtri.cliente.id.toString())
    }

    if (filtri.reparto && filtri.reparto.length > 0) {
        params.append("reparto", filtri.reparto.join(","));
    }

    if (filtri.operatori && filtri.operatori.length > 0) {
        params.append("operatore", filtri.operatori.join(","));
    }

    if (filtri.non_perfettamente_schedulati) {
        params.append("non_perfettamente_schedulati", "true");
    }

    // ========== PAGINAZIONE E RICERCA (sempre presenti) ==========

    if (filtri.page) {
        params.append("page", filtri.page.toString());
    }
    if (filtri.numero_record) {
        params.append("numero_record", filtri.numero_record.toString());
    }
    if (filtri.search) {
        params.append("search", filtri.search);
    }

    if (gotoId !== undefined && gotoId !== null) {
        params.append("goto_id", gotoId.toString());
    }

    return params.toString();
};

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Fetch lista paginata di OdP Legacy.
 * Se gotoId è valorizzato, il backend sovrascrive `page` con quella che contiene
 * l'OdP indicato (utile per portare in vista un OdP selezionato dal Gantt).
 */
export function fetchOdPLegacy(filtri: FiltriOdPLegacy = {}, gotoId?: number) {
    let url = baseUrl();
    const queryString = buildQueryString(filtri, gotoId);
    if (queryString) {
        url += `?${queryString}`;
    }

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get singolo OdP Legacy by ID (con fasi nestate)
 */

export function getOdPLegacy(odpLegacyId: number) {
    const url = baseUrl(odpLegacyId);

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

export function seguiOdPLegacy(esercizio: number, tipo_documento: number, numero_registrazione: number): Promise<SeguiOdPLegacyResponse> {
    const url = process.env.API_URL + `/api/produzione/segui-odp-legacy/${esercizio}/${tipo_documento}/${numero_registrazione}/`;

    return fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}


export function aggiornaOdPLegacy() {
    const url = process.env.API_URL + `/api/produzione/aggiorna-odp-legacy/`;

    return fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * PATCH dedicato all'assegnazione operatore di una FaseLegacy.
 * Il backend espone solo questo campo come modificabile sul mirror.
 */
export function saveFaseLegacyOperatore(
    odpId: number,
    faseId: number,
    operatoreId: number | null,
) {
    const url = process.env.API_URL + `/api/produzione/odp-legacy/${odpId}/fasi/${faseId}/operatore/`;

    return fetch(url, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify({ operatore: operatoreId }),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete (soft delete) OdP Legacy
 */
export function deleteOdPLegacy(odpLegacyToDelete: OdPLegacy) {
    return fetch(baseUrl(odpLegacyToDelete.id), {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}